import { FieldTimeOutlined, LockOutlined, MailOutlined, CheckCircleTwoTone } from '@ant-design/icons'
import { Button, Form, Input } from 'antd'
import React, { useEffect, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import Countdown, { zeroPad } from 'react-countdown'

import { useHandler } from './handler_provider'
import logo from './images/TruCarbon_Logo.png'
import { USER_OTP_RENEWAL_TYPES } from './config'

const COMMON_WEAK_PASSWORDS = new Set([
  'password', 'password1', 'password12', 'password123', 'password1234',
  'passw0rd', 'p@ssword', 'p@ssw0rd',
  'qwerty', 'qwerty1', 'qwerty12', 'qwerty123', 'qwertyuiop',
  '12345678', '123456789', '1234567890', '11111111', '00000000',
  'letmein', 'letmein1', 'welcome', 'welcome1', 'welcome123',
  'admin', 'admin1', 'admin123', 'administrator',
  'iloveyou', 'sunshine', 'monkey1', 'football',
  'trucarbon', 'trucarbon1', 'trucarbon123',
  'abc12345', 'abcdefgh',
])

const containsEmailLocalPart = (pwd, email) => {
  const local = (email || '').split('@')[0].toLowerCase().trim()
  if (local.length < 3) return false
  return pwd.toLowerCase().includes(local)
}

// Checks the SLD (2nd-level domain) of the user's email — e.g. "trucarbon"
// from "shirad@trucarbon.co". Approximates a corporate-name check for
// domain-owned emails without needing an authenticated lookup.
const containsEmailDomain = (pwd, email) => {
  const domain = (email || '').split('@')[1]?.split('.')[0]?.toLowerCase().trim()
  if (!domain || domain.length < 3) return false
  return pwd.toLowerCase().includes(domain)
}

// Detects a run of >= threshold consecutive characters whose codes are all
// ascending (+1), descending (-1), or repeating (0). Catches "1234", "dcba",
// "aaaa", and the same patterns embedded inside longer passwords.
const hasSequentialRun = (str, threshold = 4) => {
  const s = (str || '').toLowerCase()
  if (s.length < threshold) return false
  let runLen = 1
  let runDelta = null
  for (let i = 1; i < s.length; i++) {
    const delta = s.charCodeAt(i) - s.charCodeAt(i - 1)
    if (delta === 1 || delta === -1 || delta === 0) {
      if (delta === runDelta) {
        runLen++
      } else {
        runDelta = delta
        runLen = 2
      }
      if (runLen >= threshold) return true
    } else {
      runDelta = null
      runLen = 1
    }
  }
  return false
}

// 0-4 bucket scoring — rewards length + character variety, penalises
// weak/all-digits/sequential patterns. Purely presentational.
const computePasswordStrength = (pwd) => {
  if (!pwd) return 0
  let score = 0
  if (pwd.length >= 8) score += 1
  if (pwd.length >= 10) score += 1
  if (pwd.length >= 14) score += 1
  if (/[a-z]/.test(pwd)) score += 1
  if (/[A-Z]/.test(pwd)) score += 1
  if (/\d/.test(pwd)) score += 1
  if (/[^A-Za-z0-9]/.test(pwd)) score += 1
  if (COMMON_WEAK_PASSWORDS.has(pwd.toLowerCase())) score = 0
  else if (/^\d+$/.test(pwd)) score = Math.min(score, 1)
  else if (hasSequentialRun(pwd, 4)) score = Math.min(score, 2)
  if (score >= 7) return 4
  if (score >= 5) return 3
  if (score >= 3) return 2
  return 1
}

const PasswordStrengthMeter = ({ pwd }) => {
  if (!pwd) return null
  const filled = computePasswordStrength(pwd)
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong']
  const colors = ['', '#dc3545', '#fd7e14', '#ffc107', '#28a745']
  const color = colors[filled]
  return (
    <div style={{ marginTop: 4, marginBottom: 8, fontFamily: 'Muli' }}>
      <div style={{ display: 'flex', gap: 4, height: 4 }}>
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              background: i <= filled ? color : '#e5e7eb',
              borderRadius: 2,
            }}
          />
        ))}
      </div>
      <small style={{ display: 'block', marginTop: 4, color, fontWeight: 500 }}>
        {labels[filled]}
      </small>
    </div>
  )
}

export default function ForgotPassword() {
  const handler = useHandler()
  const [lexicon, setLexicon] = useState({})
  const [otpRenewalResult, setOtpRenewalResult] = useState(null)

  const inputOtpRef = useRef()
  const countDownRef = useRef()

  const [form] = Form.useForm()
  const [pwd, setPwd] = useState("");
  const meetsLength = pwd.length >= 8;
  const hasUpper = /[A-Z]/.test(pwd);
  const hasSpecial = /[^A-Za-z0-9]/.test(pwd);

  const emailValue = Form.useWatch("email", form);
  const [submittable, setSubmittable] = useState(false);

  const onFieldsChange = () => {
    let requiredFields = [];
    if (!otpRenewalResult) {
      // Step 0 — only email is required
      requiredFields = ["email"];
    } else {
      // Step 1 — email + new password + otp
      requiredFields = ["email", "password", "otp"];
    }

    const allRequiredFilled = requiredFields.every((field) => {
      const value = form.getFieldValue(field);
      return value !== undefined && value !== "";
    });

    const hasErrors = form
      .getFieldsError(requiredFields)
      .some(({ errors }) => errors.length > 0);

    setSubmittable(allRequiredFilled && !hasErrors);
  };

  useEffect(() => {
    handler.subToLexicon(setLexicon)
    return () => handler.unsubFromLexicon(setLexicon)
  }, [handler])

  useEffect(() => {
    const setValidOtpRenewalResult = (result) => {
      result && setOtpRenewalResult(result)
    }
    handler.subToOtpRenewalResult(setValidOtpRenewalResult)
    return () => handler.unsubFromOtpRenewalResult(setValidOtpRenewalResult)
  }, [handler])

  useEffect(() => {
    if (otpRenewalResult && countDownRef && countDownRef.current) {
      countDownRef.current.start()
    }
  }, [otpRenewalResult, countDownRef])

  const renderer = ({ minutes, seconds, completed, props }) => {
    if (completed) {
      return (
        <div className='d-flex justify-content-between'>
          <p className="mb-0 text-muted font-muli">
            Didn't get the email?
            <br />
            You can request a new code in {zeroPad(minutes)}:{zeroPad(seconds)}
          </p>
          <button
            type="button"
            className="fw-semibold text-primary text-decoration-underline font-muli"
            aria-disabled={false}
            onClick={() => {
              const fields = props.getFieldsValue(true)
              const emailValue = fields.email
              if (!emailValue) {
                return null
              } else {
                handler.requestUserOtpRenewal({
                  email: emailValue,
                  type: USER_OTP_RENEWAL_TYPES.FORGOT_PASSWORD,
                })
                if (inputOtpRef?.current) {
                  props.setFieldsValue({ otp: '' })
                  inputOtpRef.current.focus({
                    cursor: 'end',
                  })
                }
              }
            }}
          >
            {lexicon.resend}
          </button>
        </div>
      )
    } else {
      return (
        <div className='d-flex justify-content-between'>
          <p className="mb-0 text-muted font-muli">
            Didn't get the email?
            <br />
            You can request a new code in {zeroPad(minutes)}:{zeroPad(seconds)}
          </p>
          <button
            type="button"
            className="fw-semibold text-primary text-decoration-underline text-muted font-muli"
            aria-disabled={true}
            disabled={true}
          >
            {lexicon.resend}
          </button>
        </div >
      )
    }
  }

  const Content = (page) => {
    switch (page) {
      case 0:
        return (
          <>
            <div
              className="alert alert-borderless alert-warning text-center mb-2 mx-2 text-base font-muli"
              role="alert"
            >
              {lexicon.enter_your_email_and_instructions_will_be_sent_to_you}!
            </div>
            <div className="p-2">
              <Form
                form={form}
                onFieldsChange={onFieldsChange}
                layout="vertical"
                name="loginForm"
                className="login-form"
                onFinish={(values) => {
                  handler.requestUserOtpRenewal({ email: values.email })
                }}
              >
                <Form.Item
                  label={<span className="font-muli font-bold">{lexicon.email}</span>}
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter your Email',
                    },
                    {
                      type: 'email',
                      message: 'Invalid Email',
                    },
                  ]}
                >
                  <Input
                    prefix={
                      <MailOutlined
                        style={{ color: 'lightGray' }}
                        className="site-form-item-icon"
                      />
                    }
                    placeholder={lexicon.email}
                    className="font-muli"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="bg-[#E17E7F] font-muli"
                    disabled={!submittable}
                    block
                  >
                    {lexicon.send_otp}
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </>
        )
      case 1:
        return (
          <>
            <div
              className="alert alert-borderless alert-warning text-center text-base mb-2 mx-2 font-muli"
              role="alert"
            >
              {lexicon.please_enter_your_new_password}
            </div>
            <div className="p-2">
              <Form
                form={form}
                onFieldsChange={onFieldsChange}
                layout="vertical"
                name="loginForm"
                className="login-form"
                onFinish={(values) => {
                  handler.requestUserPasswordRenewal({
                    email: values.email,
                    password: values.password,
                    otp: values.otp,
                  })
                }}
              >
                <Form.Item
                  label={<span className="font-muli font-bold">{lexicon.email}</span>}
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: 'Please input your Email!',
                    },
                    {
                      type: 'email',
                      message: 'Wrong Email Format',
                    },
                  ]}
                >
                  <Input
                    prefix={
                      <MailOutlined
                        style={{ color: 'lightGray' }}
                        className="site-form-item-icon"
                      />
                    }
                    placeholder={lexicon.email}
                    className="font-muli"
                    disabled={!!emailValue}
                  />
                </Form.Item>
                <Form.Item
                  className="custom-label"
                  label={<span className="font-muli font-bold">{lexicon.new_password}</span>}
                  name="password"
                  dependencies={['email']}
                  rules={[
                    { required: true, message: 'Please enter your password' },
                    {
                      validator: (_, value) => {
                        const v = value || "";
                        if (v.length >= 8 && /[A-Z]/.test(v) && /[^A-Za-z0-9]/.test(v)) {
                          return Promise.resolve();
                        }
                        return Promise.reject('');
                      },
                    },
                    {
                      validator: (_, value) => {
                        const v = value || ''
                        if (v.length < 8) return Promise.reject('')
                        if (!/[A-Z]/.test(v)) return Promise.reject('')
                        if (!/[^A-Za-z0-9]/.test(v)) return Promise.reject('')
                        if (/^\d+$/.test(v)) return Promise.reject('Please avoid using only numbers.')
                        if (hasSequentialRun(v, 4)) return Promise.reject('Please avoid obvious sequences like 1234 or abcd.')
                        if (COMMON_WEAK_PASSWORDS.has(v.toLowerCase())) return Promise.reject('This password is too common. Please choose another one.')
                        if (containsEmailLocalPart(v, form.getFieldValue('email'))) return Promise.reject('Please do not use your email address in your password.')
                        if (containsEmailDomain(v, form.getFieldValue('email'))) return Promise.reject('Please do not use your company or email domain in your password.')
                        return Promise.resolve()
                      },
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: 'lightGray' }}
                      className="site-form-item-icon" />}
                    type="password"
                    placeholder={lexicon.password}
                    value={pwd}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPwd(v);
                      form.setFieldsValue({ password: v });
                    }}
                    className="font-muli"
                  />
                </Form.Item>
                <PasswordStrengthMeter pwd={pwd} />
                <div className="font-muli mb-3">
                  <ul className="space-y-1 text-sm">
                    <li className={`flex items-center gap-2 ${meetsLength ? "text-green-600" : "text-gray-600"}`}>
                      {meetsLength
                        ? <CheckCircleTwoTone twoToneColor="#16a34a" />
                        :
                        <span className="w-2 h-2 bg-gray-400 rounded-full inline-block"></span>}
                      <span className="font-muli">Minimum 8 characters</span>
                    </li>
                    <li className={`flex items-center gap-2 ${hasUpper ? "text-green-600" : "text-gray-600"}`}>
                      {hasUpper
                        ? <CheckCircleTwoTone twoToneColor="#16a34a" />
                        :
                        <span className="w-2 h-2 bg-gray-400 rounded-full inline-block"></span>}
                      <span className="font-muli">At least 1 capital letter</span>
                    </li>
                    <li className={`flex items-center gap-2 ${hasSpecial ? "text-green-600" : "text-gray-600"}`}>
                      {hasSpecial
                        ? <CheckCircleTwoTone twoToneColor="#16a34a" />
                        :
                        <span className="w-2 h-2 bg-gray-400 rounded-full inline-block"></span>}
                      <span className="font-muli">At least 1 special character</span>
                    </li>
                  </ul>
                </div>

                <Form.Item
                  label={<span className="font-muli font-bold">OTP</span>}
                  name="otp"
                  className='mb-2'
                  rules={[
                    {
                      required: true,
                      message: 'Please input your OTP!',
                    },
                  ]}
                >
                  <Input
                    prefix={
                      <FieldTimeOutlined
                        style={{ color: 'lightGray' }}
                        className="site-form-item-icon"
                      />
                    }
                    type="text"
                    placeholder="OTP"
                    ref={inputOtpRef}
                    className="font-muli"
                  />
                </Form.Item>


                <Form.Item dependencies={['email']} className='mb-1'>
                  {({ getFieldsValue, setFieldsValue }) =>
                    otpRenewalResult ? (
                      <Countdown
                        key={new Date(otpRenewalResult.otpExpiry)}
                        ref={countDownRef}
                        date={new Date(otpRenewalResult.otpExpiry)}
                        renderer={renderer}
                        autoStart={false}
                        getFieldsValue={getFieldsValue}
                        setFieldsValue={setFieldsValue}
                      >
                      </Countdown>
                    ) : <></>
                  }
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="bg-[#E17E7F] font-muli"
                    disabled={!submittable}
                    block
                    disabled={!submittable}
                  >
                    {lexicon.change_password}
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </>
        )
      default:
        return <></>
    }
  }

  return <div className="auth-page-wrapper pt-5">
    <div className="auth-one-bg-position auth-one-bg" id="auth-particles">
      <div className="bg-overlay"></div>
      <div className="shape">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
          viewBox="0 0 1440 120"
        >
          <path d="M 0,36 C 144,53.6 432,123.2 720,124 C 1008,124.8 1296,56.8 1440,40L1440 140L0 140z"></path>
        </svg>
      </div>
    </div>

    <div className="auth-page-content">
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            <div className="text-center mt-sm-5 mb-4 text-white-50">
              <div>
                <Link to="/" className="d-inline-block auth-logo">
                  <img className="max-h-12" src={logo} alt="" height="20" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6 col-xl-5">
            <div className="card mt-4">
              <div className="card-body p-4">
                <div className="text-center mt-2">
                  <h5 className="text-primary !text-[#0a564b] muli font-muli text-2xl font-bold">
                    {lexicon.forgot_password}?
                  </h5>
                  <lord-icon
                    src={'https://cdn.lordicon.com/rhvddzym.json'}
                    trigger="loop"
                    colors="primary:#0A564B"
                    className="avatar-xl"
                    style={{ height: '100px', width: '100px' }}
                  ></lord-icon>
                </div>

                {Content(!otpRenewalResult ? 0 : 1)}
              </div>
            </div>

            <div className="mt-4 text-center">
              <p className="mb-0 font-muli">
                {lexicon.take_me_back_to}{' '}
                <Link
                  to="/login"
                  className="fw-semibold text-decoration-underline text-[#E17E7F] hover:text-[#E17E7F] font-muli"
                >
                  {lexicon.sign_in_page}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <footer className="footer">
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            <div className="text-center">
              <p className="mb-0 text-muted font-muli">
                &copy;&nbsp;{new Date().getFullYear()} TruCarbon.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
}
